import { Shield, User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Badge } from "@/components/ui/badge";

const INTENT_LABELS: Record<string, { label: string; color: string }> = {
  command_lookup: { label: "CLI Commands", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  troubleshooting: { label: "Playbooks", color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  cve_security: { label: "CVEs", color: "bg-red-500/15 text-red-400 border-red-500/30" },
  cloud_networking: { label: "Cloud", color: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  kb_articles: { label: "Knowledge Base", color: "bg-violet-500/15 text-violet-400 border-violet-500/30" },
  port_protocol: { label: "Ports", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" },
  incident: { label: "Incidents", color: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  config_template: { label: "Config Templates", color: "bg-lime-500/15 text-lime-400 border-lime-500/30" },
  compliance: { label: "Compliance", color: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30" },
  command_output: { label: "CLI Outputs", color: "bg-teal-500/15 text-teal-400 border-teal-500/30" },
  config_snapshot: { label: "Config Snapshots", color: "bg-pink-500/15 text-pink-400 border-pink-500/30" },
  doc_search: { label: "Documents", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  general: { label: "General", color: "bg-muted text-muted-foreground border-border" },
};

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
  intents?: string[];
}

const ChatMessage = ({ role, content, timestamp, intents }: ChatMessageProps) => {
  const isAssistant = role === "assistant";

  return (
    <div className={`flex gap-3 animate-slide-up ${isAssistant ? "" : "flex-row-reverse"}`}>
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
          isAssistant
            ? "bg-primary/10 glow-border border"
            : "bg-secondary"
        }`}
      >
        {isAssistant ? (
          <Shield className="w-4 h-4 text-primary" />
        ) : (
          <User className="w-4 h-4 text-secondary-foreground" />
        )}
      </div>
      <div
        className={`max-w-[75%] rounded-lg px-4 py-3 ${
          isAssistant
            ? "bg-card border border-border"
            : "bg-secondary"
        }`}
      >
        {isAssistant ? (
          <div className="prose prose-sm prose-invert max-w-none text-foreground
            prose-headings:text-foreground prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1
            prose-h3:text-sm prose-h2:text-base
            prose-p:text-sm prose-p:leading-relaxed prose-p:my-1
            prose-li:text-sm prose-li:my-0.5
            prose-ul:my-1 prose-ol:my-1
            prose-strong:text-primary prose-strong:font-semibold
            prose-code:text-primary prose-code:bg-primary/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono prose-code:before:content-none prose-code:after:content-none
            prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:rounded-md prose-pre:my-2
            prose-a:text-primary prose-a:underline prose-a:underline-offset-2 hover:prose-a:opacity-80
          ">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        ) : (
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{content}</p>
        )}
        {isAssistant && intents && intents.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-border/50">
            {intents.map((intent) => {
              const meta = INTENT_LABELS[intent] || { label: intent, color: "bg-muted text-muted-foreground border-border" };
              return (
                <span
                  key={intent}
                  className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border ${meta.color}`}
                >
                  {meta.label}
                </span>
              );
            })}
          </div>
        )}
        {timestamp && (
          <span className="text-xs text-muted-foreground mt-2 block font-mono">
            {timestamp}
          </span>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
